/**
 * filterBarHandler.js
 *
 * Calendar Filter Bar Handler (ES Module)
 *
 * Responsibilities:
 * - Bind filter UI controls (type, month, year, search, date range).
 * - Toggle custom date inputs.
 * - Update the "active filters" status UI.
 * - Validate custom date range before applying.
 * - Build navigation URLs while preserving view state.
 */

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

export class FilterBarHandler {
  constructor({ debug = false, autoInit = true } = {}) {
    this.debug = debug;
    this.autoInit = autoInit;

    this.initialized = false;
    this._domReadyListenerRegistered = false;

    // DOM refs
    this.filterBar = null;
    this.filterForm = null;
    this.typeSelect = null;
    this.monthSelect = null;
    this.yearSelect = null;
    this.searchInput = null;

    this.dateRangeSelect = null;
    this.customDateSection = null;
    this.startDateInput = null;
    this.endDateInput = null;

    this.applyButton = null;
    this.clearButton = null;
    this.removeFiltersButton = null;

    this.filterStatus = null;
    this.activeFiltersSpan = null;
    this.validationMessageEl = null;

    if (this.autoInit) this.initialize();
  }

  log(...args) {
    if (this.debug) console.log('🧰 FilterBarHandler:', ...args);
  }

  isCalendarPage() {
    return Boolean(
      document.querySelector('#calendar-section') ||
      document.querySelector('.toggle-buttons-container') ||
      document.querySelector('#calendar') ||
      document.querySelector('.calendar-calendar, .calendar-list')
    );
  }

  /**
   * Public, idempotent init.
   */
  initialize({ debug } = {}) {
    if (!isBrowser) return false;
    if (typeof debug === 'boolean') this.debug = debug;

    if (this.initialized) return true;

    if (!this.isCalendarPage()) {
      this.log('Not a calendar page; skipping.');
      return false;
    }

    if (document.readyState === 'loading') {
      if (!this._domReadyListenerRegistered) {
        this._domReadyListenerRegistered = true;
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            this.setupElements();
          },
          { once: true }
        );
      }
      return true;
    }

    return this.setupElements();
  }

  setupElements() {
    this.filterBar = document.querySelector('.filter-bar');
    if (!this.filterBar) {
      this.log('Filter bar not found; skipping.');
      return false;
    }

    this.filterForm = document.querySelector('.calendar-filters');

    // Core filters
    this.typeSelect = document.querySelector('#type');
    this.monthSelect = document.querySelector('#month');
    this.yearSelect = document.querySelector('#year');
    this.searchInput = document.querySelector('#search');

    // Date range filters (optional depending on template)
    this.dateRangeSelect = document.querySelector('#date-range-filter');
    this.customDateSection = document.querySelector('.custom-date-section');
    this.startDateInput = document.getElementById('start-date');
    this.endDateInput = document.getElementById('end-date');

    // Buttons (optional)
    this.applyButton = document.querySelector('.calendarfilter-btn');
    this.clearButton = document.getElementById('clear-filters');
    this.removeFiltersButton = document.getElementById('remove-filters');

    // Status UI (optional)
    this.filterStatus = document.getElementById('filter-status');
    this.activeFiltersSpan = document.getElementById('active-filters');

    // Inline validation message (only if status container exists)
    if (this.filterStatus && !this.validationMessageEl) {
      this.validationMessageEl = document.createElement('div');
      this.validationMessageEl.setAttribute('role', 'status');
      this.validationMessageEl.style.marginTop = '6px';
      this.validationMessageEl.style.display = 'none';
      this.filterStatus.appendChild(this.validationMessageEl);
    }

    this.bindEvents();
    this.toggleCustomDateSection();
    this.updateFilterStatus();

    this.initialized = true;
    this.log('Initialized.');
    return true;
  }

  bindEvents() {
    // Toggle custom date inputs
    if (this.dateRangeSelect && this.customDateSection) {
      this.dateRangeSelect.addEventListener('change', () => {
        this.toggleCustomDateSection();
        this.clearValidation();
        this.updateFilterStatus();
      });
    }

    // Live status updates (change)
    [this.typeSelect, this.monthSelect, this.yearSelect].filter(Boolean).forEach(el => {
      el.addEventListener('change', () => this.updateFilterStatus());
    });

    // Search: keep in sync while typing
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.updateFilterStatus());
      this.searchInput.addEventListener('change', () => this.updateFilterStatus());
    }

    // Dates: use input to avoid lag while typing/picking
    [this.startDateInput, this.endDateInput].filter(Boolean).forEach(el => {
      el.addEventListener('input', () => {
        this.clearValidation();
        this.updateFilterStatus();
      });
      el.addEventListener('change', () => {
        this.clearValidation();
        this.updateFilterStatus();
      });
    });

    // Optional JS navigation buttons
    if (this.applyButton) {
      this.applyButton.addEventListener('click', e => {
        // If the button is a submit button, don't interfere.
        if (this.applyButton.type !== 'submit') {
          e.preventDefault();
          this.applyFilters();
        }
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', e => {
        e.preventDefault();
        this.clearFilters();
      });
    }

    if (this.removeFiltersButton) {
      this.removeFiltersButton.addEventListener('click', e => {
        e.preventDefault();
        this.clearFilters();
      });
    }
  }

  toggleCustomDateSection() {
    if (!this.customDateSection || !this.dateRangeSelect) return;

    const isCustom = this.dateRangeSelect.value === 'custom';
    this.customDateSection.style.display = isCustom ? 'block' : 'none';

    // Clear custom dates if switching away from custom
    if (!isCustom) {
      if (this.startDateInput) this.startDateInput.value = '';
      if (this.endDateInput) this.endDateInput.value = '';
    }
  }

  getFilterValues() {
    const dateRange = this.dateRangeSelect ? this.dateRangeSelect.value : '';
    const startDate = this.startDateInput ? this.startDateInput.value : '';
    const endDate = this.endDateInput ? this.endDateInput.value : '';

    return {
      type: this.typeSelect ? this.typeSelect.value : '',
      month: this.monthSelect ? this.monthSelect.value : '',
      year: this.yearSelect ? this.yearSelect.value : '',
      search: this.searchInput ? this.searchInput.value : '',
      dateRange,
      startDate,
      endDate
    };
  }

  getCurrentView() {
    // Preserve the current view state (calendar or list) if available
    if (window.toggleHandler && typeof window.toggleHandler.getCurrentView === 'function') {
      return window.toggleHandler.getCurrentView();
    }
    const viewParam = new URL(window.location.href).searchParams.get('view');
    return viewParam || '';
  }

  buildUrlWithFilters(filters) {
    const url = new URL(window.location.href);

    // Clear existing filter parameters and pagination
    ['type', 'month', 'year', 'search', 'dateRange', 'startDate', 'endDate', 'page'].forEach(param => {
      url.searchParams.delete(param);
    });

    // Add new filter parameters (only if they have values)
    Object.entries(filters).forEach(([key, val]) => {
      if (val) url.searchParams.set(key, val);
    });

    // Preserve view state
    const currentView = this.getCurrentView();
    if (currentView) url.searchParams.set('view', currentView);

    return url;
  }

  showValidation(message) {
    if (!this.validationMessageEl) return;
    this.validationMessageEl.textContent = message;
    this.validationMessageEl.style.display = 'block';
  }

  clearValidation() {
    if (!this.validationMessageEl) return;
    this.validationMessageEl.textContent = '';
    this.validationMessageEl.style.display = 'none';
  }

  validateFilters(filters) {
    if (filters.dateRange !== 'custom') return { ok: true };

    // If custom date range is selected, require both dates
    if (!filters.startDate || !filters.endDate) {
      return { ok: false, message: 'Please choose both a start and end date for a custom range.' };
    }

    // Ensure start <= end
    if (filters.startDate > filters.endDate) {
      return { ok: false, message: 'Start date must be on or before the end date.' };
    }

    return { ok: true };
  }

  applyFilters() {
    const filters = this.getFilterValues();
    const validation = this.validateFilters(filters);

    if (!validation.ok) {
      this.showValidation(validation.message || 'Invalid filter selection.');
      this.log('Validation failed:', validation);
      return;
    }

    this.clearValidation();

    const normalized = { ...filters };
    if (normalized.dateRange !== 'custom') {
      normalized.startDate = '';
      normalized.endDate = '';
    }

    const url = this.buildUrlWithFilters(normalized);
    this.log('Navigating to:', url.toString());
    window.location.href = url.toString();
  }

  clearFilters() {
    this.clearValidation();

    // Reset UI
    if (this.typeSelect) this.typeSelect.value = '';
    if (this.monthSelect) this.monthSelect.value = '';
    if (this.yearSelect) this.yearSelect.value = '';
    if (this.searchInput) this.searchInput.value = '';
    if (this.dateRangeSelect) this.dateRangeSelect.value = '';
    if (this.startDateInput) this.startDateInput.value = '';
    if (this.endDateInput) this.endDateInput.value = '';
    this.toggleCustomDateSection();

    const url = this.buildUrlWithFilters({
      type: '',
      month: '',
      year: '',
      search: '',
      dateRange: '',
      startDate: '',
      endDate: ''
    });

    this.log('Navigating to (cleared):', url.toString());
    window.location.href = url.toString();
  }

  updateFilterStatus() {
    if (!this.filterStatus || !this.activeFiltersSpan) return;

    const filters = this.getFilterValues();
    const active = [];

    // Type
    if (filters.type && this.typeSelect) {
      const opt = this.typeSelect.querySelector(`option[value="${CSS.escape(filters.type)}"]`);
      active.push(`Type: ${opt ? opt.textContent : filters.type}`);
    }

    // Month/Year
    if (filters.month) active.push(`Month: ${filters.month}`);
    if (filters.year) active.push(`Year: ${filters.year}`);

    // Search
    if (filters.search) active.push(`Search: ${filters.search}`);

    // Date range
    if (filters.dateRange && this.dateRangeSelect) {
      if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
        active.push(`Date: ${filters.startDate} → ${filters.endDate}`);
      } else if (filters.dateRange !== 'custom') {
        const opt = this.dateRangeSelect.querySelector(
          `option[value="${CSS.escape(filters.dateRange)}"]`
        );
        active.push(`Date: ${opt ? opt.textContent : filters.dateRange}`);
      }
    }

    if (active.length) {
      this.activeFiltersSpan.textContent = active.join(', ');
      this.filterStatus.style.display = 'block';
    } else {
      this.filterStatus.style.display = 'none';
      this.activeFiltersSpan.textContent = '';
    }
  }

  // Public API
  getCurrentFilters() {
    return this.getFilterValues();
  }
}

const filterBarHandler = new FilterBarHandler({ debug: false, autoInit: true });

// Backward-compatible globals (no-op in SSR)
if (isBrowser) {
  window.FilterBarHandler = FilterBarHandler;
  window.filterBarHandler = filterBarHandler;
}

export default filterBarHandler;
